// src/app/constants/prompt-tracking-constants.ts

import { Search, Target, BarChart3, MessageCircle } from 'lucide-react';
import type { PromptIntent, PromptStatus, TrendDirection } from '../types/prompt-tracking-types';
import {
  MEASUREMENT_MODELS,
  MEASUREMENT_DEFAULT_MODELS,
  getModelDisplayName,
  type MeasurementModel,
} from './models';

// ---------------------------------------------------------------------------
// Intent configuration
// ---------------------------------------------------------------------------

export interface IntentConfig {
  key: PromptIntent;
  label: string;
  shortLabel: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  color: string;
  bgColor: string;
  description: string;
}

export const INTENT_CONFIG: IntentConfig[] = [
  {
    key: 'buying',
    label: 'Buying Intent',
    shortLabel: 'Buying',
    icon: Target,
    color: 'var(--signal-blue)',
    bgColor: 'var(--signal-blue-10)',
    description: 'User is ready to purchase or evaluate solutions',
  },
  {
    key: 'research',
    label: 'Research Intent',
    shortLabel: 'Research',
    icon: Search,
    color: 'var(--signal-blue)',
    bgColor: 'var(--signal-blue-25)',
    description: 'User is learning about concepts and best practices',
  },
  {
    key: 'comparison',
    label: 'Comparison Intent',
    shortLabel: 'Comparison',
    icon: BarChart3,
    color: 'var(--signal-blue)',
    bgColor: 'var(--signal-blue-50)',
    description: 'User is comparing you against specific competitors',
  },
  {
    key: 'validation',
    label: 'Validation Intent',
    shortLabel: 'Validation',
    icon: MessageCircle,
    color: 'var(--alpha-teal)',
    bgColor: 'var(--alpha-teal-10)',
    description: 'User is confirming or second-guessing a decision',
  },
];

export function getIntentConfig(key: PromptIntent): IntentConfig | undefined {
  return INTENT_CONFIG.find((c) => c.key === key);
}

// ---------------------------------------------------------------------------
// Available AI models
//
// Derived from the central measurement-model set. IDs MUST match the
// provider keys accepted by call_llm_async on the backend, which is
// guaranteed by `MeasurementModel` from @/app/constants/models.
// ---------------------------------------------------------------------------

export interface AvailableModel {
  id: MeasurementModel;   // sent to the backend as-is
  name: string;           // display label
}

export const AVAILABLE_MODELS: AvailableModel[] = MEASUREMENT_MODELS.map((id) => ({
  id,
  name: getModelDisplayName(id),
}));

// Every OFFERED lane is listed in AVAILABLE_MODELS above; only the default-on
// subset is pre-ticked. Opt-in lanes (aimode) appear unchecked rather than
// silently running on every tracked prompt.
export const DEFAULT_SELECTED_MODELS: MeasurementModel[] = [...MEASUREMENT_DEFAULT_MODELS];

// ---------------------------------------------------------------------------
// Status badge styles
// ---------------------------------------------------------------------------

export interface StatusStyle {
  backgroundColor: string;
  color: string;
  label: string;
}

export const STATUS_STYLES: Record<PromptStatus, StatusStyle> = {
  active: {
    backgroundColor: 'var(--success-bg)',
    color: 'var(--success-text)',
    label: 'Active',
  },
  paused: {
    backgroundColor: 'var(--muted)',
    color: 'var(--muted-foreground)',
    label: 'Paused',
  },
  needs_attention: {
    backgroundColor: 'var(--warning-bg)',
    color: 'var(--warning-text)',
    label: 'Attention',
  },
};

export function getStatusStyle(status: PromptStatus): StatusStyle {
  return STATUS_STYLES[status] ?? STATUS_STYLES.active;
}

// ---------------------------------------------------------------------------
// Trend indicator styles
// ---------------------------------------------------------------------------

export interface TrendConfig {
  color: string;
}

export const TREND_CONFIG: Record<TrendDirection, TrendConfig> = {
  up:     { color: 'var(--success-text)' },
  down:   { color: 'var(--infrared-coral)' },
  stable: { color: 'var(--muted-foreground)' },
};

export function getTrendConfig(trend: TrendDirection): TrendConfig {
  return TREND_CONFIG[trend] ?? TREND_CONFIG.stable;
}

// ---------------------------------------------------------------------------
// Recommendation rate color thresholds
// ---------------------------------------------------------------------------

export function getRecRateColor(rate: number): string {
  if (rate >= 70) return 'var(--success-text)';
  if (rate >= 50) return 'var(--warning-text)';
  return 'var(--infrared-coral)';
}

// ---------------------------------------------------------------------------
// Model position badge styles
// ---------------------------------------------------------------------------

export function getPositionStyle(position: number): { backgroundColor: string; color: string } {
  if (position <= 3) return { backgroundColor: 'var(--success-bg)', color: 'var(--success-text)' };
  if (position <= 5) return { backgroundColor: 'var(--signal-blue-10)', color: 'var(--signal-blue)' };
  return { backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' };
}

// ---------------------------------------------------------------------------
// Sentiment color
// ---------------------------------------------------------------------------

export function getSentimentColor(sentiment: number): string {
  if (sentiment >= 75) return 'var(--success-text)';
  if (sentiment >= 50) return 'var(--alpha-teal)';
  return 'var(--muted-foreground)';
}

// ---------------------------------------------------------------------------
// Default expanded group state
// ---------------------------------------------------------------------------

export const DEFAULT_EXPANDED_GROUPS: Record<PromptIntent, boolean> = {
  buying:     true,
  research:   true,
  comparison: true,
  validation: true,
};

// ---------------------------------------------------------------------------
// Briefing tab config
// ---------------------------------------------------------------------------

export const BRIEFING_TABS = [
  { key: 'questions' as const, label: 'Ask Questions' },
  { key: 'briefing'  as const, label: 'Prompt Briefing' },
] as const;

export type BriefingTabKey = (typeof BRIEFING_TABS)[number]['key'];

// ---------------------------------------------------------------------------
// AI questions textarea placeholder
// ---------------------------------------------------------------------------

export const AI_QUESTIONS_PLACEHOLDER =
  'e.g., Why are my buying-intent prompts underperforming? Which prompts should I prioritize this week?';